/**
 * lotto-script-guardian.js  v2.0
 * ════════════════════════════════════════════════════════════════════════════
 * PLACE THIS AS THE VERY FIRST <script> TAG IN <head> — before everything.
 *
 * ── WHAT IT DOES ────────────────────────────────────────────────────────────
 *  1. Overrides document.createElement to intercept dynamically injected
 *     scripts before they touch the network.
 *  2. Stubs analytics globals (fbq, clarity, dataLayer, moe_web_sdk) so
 *     inline init code queues calls safely without the real SDKs loading.
 *  3. Converts render-blocking <script src> tags to non-blocking on the fly
 *     using a MutationObserver.
 *  4. On first user interaction (scroll/click/touch/key), loads everything
 *     in original order.
 *  5. 6-second hard fallback for sessions with no interaction.
 *
 * ── SCRIPTS DEFERRED (found in lottosport.in audit) ────────────────────────
 *   Analytics    : Google Tag Manager, Meta/FB Pixel, Microsoft Clarity
 *                  Google gtag (DC-15443236)
 *   CRM          : MoEngage (2 scripts)
 *   Personaliz.  : Spectrum AI (bootstrap — was render-blocking!, analytics,
 *                  cart, integrations)
 *   Attribution  : Trackier SDK  ← was render-blocking!
 *   Reviews      : Judge.me widget preloader
 *   Payments     : Snapmint installments widget
 *   Referral     : ReferRush floating-popup + contact-form (JS)
 *   Growth       : CartBite / Grow (staq-cdn, staqlab)
 *   Wishlist     : (custom, handled via custom.js — see SAFE list)
 *
 * ── SAFE — NEVER DEFERRED ───────────────────────────────────────────────────
 *   Gokwik / Kwikpass (checkout + login auth)
 *   Shopify core (compiled_assets/scripts.js, checkouts/internal, shopifycloud)
 *   Shopify theme assets (custom.js, custom-dev.js, predictive-search.js, etc.)
 *   Shopify CDN files (cdn.shopify.com core, shopifycloud)
 *   Lazysizes, jquery-slick (UI function — already deferred/safe)
 *   CustomerFields CF (inline JSON, cannot be deferred — reduce via app settings)
 * ════════════════════════════════════════════════════════════════════════════
 */

  (function () {
    'use strict';

    /* ─── 0. Guard: run only once ───────────────────────────────────────────── */
    if (window.__lottoGuardianActive) return;
    window.__lottoGuardianActive = true;

    /* ─── 1. Config ─────────────────────────────────────────────────────────── */

    /**
     * URL substrings that mark a <script src> as non-essential.
     * Match is substring — keep patterns specific enough to avoid false positives.
     */
    var DEFER_PATTERNS = [
      // ── Analytics
      'googletagmanager.com/gtm',           // GTM loader
      'googletagmanager.com/gtag',          // gtag (DC / GA4)
      'connect.facebook.net',              // Meta/FB Pixel SDK
      'clarity.ms/tag',                    // Microsoft Clarity

      // ── CRM / Marketing
      'cdn.moengage.com',                  // MoEngage SDK (both scripts)

      // ── Attribution
      'trackier-websdk',                   // Trackier — was render-blocking!

      // ── Reviews
      'cdnwidget.judge.me',                // Judge.me widget preloader

      // ── Payments widget
      'checkout-merchant.snapmint.com',    // Snapmint
      // ── Referral
      'referrush-289/assets/floating-popup',
      'referrush-289/assets/contact-form',

      // ── Growth / CartBite
      's1.staq-cdn.com/grow',
      'js-server.staqlab.com',
      'cartbite',                          // cartbite-embed.js, cartbite-collection.js

      // ── Legacy / future
      'cdn.mxpnl.com',                     // Mixpanel (currently commented out)
      'hotjar.com',
      'segment.io',
      'segment.com',
    ];

    /** Interaction events that trigger the deferred load */
    var TRIGGER_EVENTS = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click'];

    /** Hard fallback delay in milliseconds */
    var FALLBACK_MS = 6000;

    /* ─── 2. State ──────────────────────────────────────────────────────────── */

    var fired  = false;
    var queue  = [];
    var origCE = document.createElement.bind(document);

    /* ─── 3. Helpers ────────────────────────────────────────────────────────── */

    function isDeferred(src) {
      if (!src || typeof src !== 'string') return false;
      // Spectrum runtime must never be deferred. spectrum-sdk.js defines
      // window.Spectrum on load; once neutralised it is re-injected via
      // setAttribute('src', …) on an in-DOM <script>, which does NOT
      // re-execute — leaving window.Spectrum undefined and every Spectrum
      // snippet (reviews, gallery, cart) broken. Always let it through.
      if (src.indexOf('spectrum') !== -1) return false;
      for (var i = 0; i < DEFER_PATTERNS.length; i++) {
        if (src.indexOf(DEFER_PATTERNS[i]) !== -1) return true;
      }
      return false;
    }

    function loadScript(cfg) {
      return new Promise(function (resolve) {
        var s = origCE('script');
        s.src     = cfg.src;
        s.async   = (cfg.async !== false);
        if (cfg.defer)       s.defer      = cfg.defer;
        if (cfg.type)        s.type       = cfg.type;
        if (cfg.crossOrigin) s.crossOrigin = cfg.crossOrigin;
        if (cfg.id)          s.id         = cfg.id;
        if (cfg.dataset) {
          Object.keys(cfg.dataset).forEach(function(k) {
            s.dataset[k] = cfg.dataset[k];
          });
        }
        s.onload  = function () { resolve(true);  if (cfg.callback) cfg.callback(); };
        s.onerror = function () { resolve(false); if (cfg.callback) cfg.callback(); };
        document.head.appendChild(s);
      });
    }

    /* ─── 4. Stub globals so inline init code doesn't throw ─────────────────── */

    // ── Facebook / Meta Pixel stub
    if (!window.fbq) {
      var fbqStub = function () {
        fbqStub.callMethod
          ? fbqStub.callMethod.apply(fbqStub, arguments)
          : fbqStub.queue.push(arguments);
      };
      fbqStub.push    = fbqStub;
      fbqStub.loaded  = true;
      fbqStub.version = '2.0';
      fbqStub.queue   = [];
      window.fbq      = fbqStub;
      window._fbq     = fbqStub;
    }

    // ── Google Tag Manager / gtag stub
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    // ── Google gtag stub (for DC-15443236 and GA4)
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    // ── Microsoft Clarity stub
    if (!window.clarity) {
      window.clarity = function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
    }

    // ── Snapmint stub
    window.snapmint = window.snapmint || { queue: [] };

    // ── MoEngage stub — prevents "Moe is not defined" errors from inline init
    if (!window.Moe) {
      window.Moe = function() {};
      window.Moe.prototype.init = function() {};
    }
    // moe_web_sdk queues calls
    if (!window.moe) {
      window.moe = function() {
        (window.moe.q = window.moe.q || []).push(arguments);
      };
    }

    // ── Spectrum AI stub — the init block runs before bootstrap.js loads
    window.__spectrumAi = window.__spectrumAi || {};

    // ── CartBite / Grow stub
    window.growAppEmbedLoaded = window.growAppEmbedLoaded || false;

    /* ─── 5. Intercept document.createElement('script') ─────────────────────── */

    document.createElement = function (tagName, options) {
      var el = origCE(tagName, options);

      if (typeof tagName !== 'string' || tagName.toLowerCase() !== 'script') {
        return el;
      }

      var _src    = '';
      var _held   = false;
      var srcDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');

      Object.defineProperty(el, 'src', {
        get: function () { return _src; },
        set: function (val) {
          if (!fired && isDeferred(val)) {
            _src  = val;
            _held = true;
            queue.push({ src: val, _el: el });
            if (!el.type) el.type = 'text/plain';
            return;
          }
          _src = val;
          srcDesc.set.call(el, val);
        },
        configurable: true
      });

      return el;
    };

    /* ─── 6. MutationObserver — catch <script src> added via innerHTML/DOM ───── */
    /*
    * Spectrum AI bootstrap.js was injected WITHOUT defer/async — this is the
    * biggest single source of TBT on your PDP. The observer neutralises it.
    */

    var domObserver = new MutationObserver(function (mutations) {
      if (fired) { domObserver.disconnect(); return; }

      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1 || node.nodeName !== 'SCRIPT') return;
          var src = node.getAttribute('src') || '';
          if (isDeferred(src) && node.type !== 'text/plain') {
            node.type = 'text/plain';
            node.removeAttribute('src');
            // Preserve data-* attributes (Spectrum uses data-spectrum-ai)
            var dataset = {};
            Array.prototype.forEach.call(node.attributes, function(attr) {
              if (attr.name.startsWith('data-')) {
                dataset[attr.name.replace('data-','')] = attr.value;
              }
            });
            queue.push({ src: src, _domNode: node, dataset: dataset });
          }
        });
      });
    });

    domObserver.observe(document.documentElement, { childList: true, subtree: true });

    /* ─── 7. Fire deferred scripts after interaction ─────────────────────────── */

    function flushQueue() {
      document.createElement = origCE;
      domObserver.disconnect();

      TRIGGER_EVENTS.forEach(function (ev) {
        window.removeEventListener(ev, onFirstInteraction, { passive: true });
      });

      if (!queue.length) return;

      setTimeout(function () {
        (function next(i) {
          if (i >= queue.length) return;
          var cfg = queue[i];

          if (cfg._domNode) {
            var node = cfg._domNode;
            node.type = '';
            // Restore data-* attributes
            if (cfg.dataset) {
              Object.keys(cfg.dataset).forEach(function(k) {
                node.setAttribute('data-' + k, cfg.dataset[k]);
              });
            }
            node.setAttribute('src', cfg.src);
            node.addEventListener('load',  function () { next(i + 1); });
            node.addEventListener('error', function () { next(i + 1); });
            return;
          }

          if (cfg._el) {
            var el = cfg._el;
            delete el.src;
            el.type = '';
            el.src = cfg.src;
            if (!el.parentNode) document.head.appendChild(el);
            el.addEventListener('load',  function () { next(i + 1); });
            el.addEventListener('error', function () { next(i + 1); });
            return;
          }

          loadScript(cfg).then(function () { next(i + 1); });
        }(0));

        console.log('[lotto-guardian] Loaded ' + queue.length + ' deferred scripts.');
      }, 200);
    }

    function onFirstInteraction() {
      if (fired) return;
      fired = true;
      flushQueue();
    }

    TRIGGER_EVENTS.forEach(function (ev) {
      window.addEventListener(ev, onFirstInteraction, { passive: true });
    });

    // Hard fallback
    setTimeout(function () {
      if (!fired) { fired = true; flushQueue(); }
    }, FALLBACK_MS);

    /* ─── 8. CSS deferral helper — call from theme.liquid for 3rd-party CSS ──── */
    /*
    * Usage: window.__lottoGuardianDeferCSS('https://...url...')
    * Call this for ReferRush, Judge.me CSS that are currently double-loaded.
    */
    window.__lottoGuardianDeferCSS = function(href) {
      if (fired) {
        var link = origCE('link');
        link.rel  = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
        return;
      }
      // Block it now, queue it
      var existingLinks = document.querySelectorAll('link[href="' + href + '"]');
      existingLinks.forEach(function(l) { l.media = 'print'; });
      queue.push({ _cssHref: href });
    };

    /* ─── 9. Expose manual trigger for debugging ─────────────────────────────── */
    window.__lottoGuardianFlush = function () {
      if (!fired) { fired = true; flushQueue(); }
    };

    console.log('[lotto-guardian] v2.0 Active. ' + DEFER_PATTERNS.length + ' patterns watched.');
    console.log('[lotto-guardian] Spectrum bootstrap.js, Trackier, MoEngage, GTM — all queued for post-interaction load.');

  }());